import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useScroll, useTransform, useSpring, useInView, useAnimation } from 'framer-motion';
// Text Reveal Animation (character by character)
export function TextReveal({
  text,
  className = '',
  delay = 0
}: {
  text: string;
  className?: string;
  delay?: number;
}) {
  const words = text.split(' ');
  const container = {
    hidden: {
      opacity: 0
    },
    visible: (i = 1) => ({
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: delay * 0.3
      }
    })
  };
  const child = {
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100
      }
    },
    hidden: {
      opacity: 0,
      y: 20,
      transition: {
        type: 'spring',
        damping: 12,
        stiffness: 100
      }
    }
  };
  return <motion.div className={`flex flex-wrap ${className}`} variants={container} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-50px'
  }} data-id="element-122">
      {words.map((word, index) => <motion.span variants={child} style={{
      marginRight: '0.25em'
    }} key={index} className="inline-block" data-id="element-123">
          {word}
        </motion.span>)}
    </motion.div>;
}
// Spotlight Card Effect (mouse tracking glow)
export function SpotlightCard({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [opacity, setOpacity] = useState(0);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current || isFocused) return;
    const div = divRef.current;
    const rect = div.getBoundingClientRect();
    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  const handleFocus = () => {
    setIsFocused(true);
    setOpacity(1);
  };
  const handleBlur = () => {
    setIsFocused(false);
    setOpacity(0);
  };
  const handleMouseEnter = () => {
    setOpacity(1);
  };
  const handleMouseLeave = () => {
    setOpacity(0);
  };
  return <div ref={divRef} onMouseMove={handleMouseMove} onFocus={handleFocus} onBlur={handleBlur} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className={`relative overflow-hidden rounded-xl border border-picus-border bg-picus-surface/30 transition-colors hover:bg-picus-surface/50 ${className}`} data-id="element-124">
      <div className="pointer-events-none absolute -inset-px opacity-0 transition duration-300" style={{
      opacity,
      background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, rgba(0,212,170,.1), transparent 40%)`
    }} data-id="element-125" />
      <div className="relative h-full" data-id="element-126">{children}</div>
    </div>;
}
// Animated Gradient Background for Hero
export function HeroBackground() {
  return <div className="absolute inset-0 overflow-hidden pointer-events-none z-0" data-id="element-127">
      <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-picus-accent/5 blur-[120px] mix-blend-screen animate-pulse" style={{
      animationDuration: '8s'
    }} data-id="element-128" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] rounded-full bg-blue-500/5 blur-[150px] mix-blend-screen animate-pulse" style={{
      animationDuration: '12s',
      animationDelay: '2s'
    }} data-id="element-129" />

      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
      backgroundSize: '40px 40px'
    }} data-id="element-130" />

      {/* Fade out at bottom */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-picus-bg/80 to-picus-bg" data-id="element-131" />
    </div>;
}
// Number Counter Animation
export function AnimatedNumber({
  value
}: {
  value: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  // Simple implementation - just fade and slide up
  return <motion.span ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.8,
    type: 'spring',
    bounce: 0.4
  }} className="inline-block" data-id="element-132">
      {value}
    </motion.span>;
}
// BlurText Animation (word by word blur reveal)
export function BlurText({
  text,
  className = '',
  delay = 0
}: {
  text: string;
  className?: string;
  delay?: number;
}) {
  const words = text.split(' ');
  const container = {
    hidden: {
      opacity: 0
    },
    visible: (i = 1) => ({
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: delay * 0.3
      }
    })
  };
  const child = {
    visible: {
      opacity: 1,
      filter: 'blur(0px)',
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    hidden: {
      opacity: 0,
      filter: 'blur(10px)',
      y: 10,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <motion.div className={`flex flex-wrap ${className}`} variants={container} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-50px'
  }} data-id="element-133">
      {words.map((word, index) => <motion.span variants={child} style={{
      marginRight: '0.25em'
    }} key={index} className="inline-block will-change-transform" data-id="element-134">
          {word}
        </motion.span>)}
    </motion.div>;
}
// ShinyText (shimmering highlight)
export function ShinyText({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <span className={`inline-block animate-shiny bg-clip-text text-transparent bg-[linear-gradient(110deg,#9ca3af,45%,#ffffff,55%,#9ca3af)] bg-[length:200%_100%] ${className}`} data-id="element-135">
      {children}
    </span>;
}
// TiltCard (3D perspective tilt on hover)
export function TiltCard({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    // Max rotation 8 degrees
    const rotateXValue = (y - centerY) / centerY * -8;
    const rotateYValue = (x - centerX) / centerX * 8;
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };
  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
  };
  return <motion.div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} animate={{
    rotateX,
    rotateY
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30,
    mass: 0.5
  }} style={{
    perspective: 1000,
    transformStyle: 'preserve-3d'
  }} className={`will-change-transform ${className}`} data-id="element-136">
      {children}
    </motion.div>;
}
// Marquee (Infinite horizontal scroll)
export function Marquee({
  children,
  className = '',
  pauseOnHover = true
}: {
  children: React.ReactNode;
  className?: string;
  pauseOnHover?: boolean;
}) {
  return <div className={`flex overflow-hidden w-full ${className}`} data-id="element-137">
      <div className={`flex shrink-0 animate-marquee min-w-full items-center justify-around gap-12 px-6 ${pauseOnHover ? 'hover:[animation-play-state:paused]' : ''}`} data-id="element-138">
        {children}
      </div>
      <div className={`flex shrink-0 animate-marquee min-w-full items-center justify-around gap-12 px-6 ${pauseOnHover ? 'hover:[animation-play-state:paused]' : ''}`} aria-hidden="true" data-id="element-139">
        {children}
      </div>
    </div>;
}
// CountUp (Animated number counter)
export function CountUp({
  end,
  suffix = '',
  prefix = '',
  duration = 2,
  className = ''
}: {
  end: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
  className?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (!isInView) return;
    let startTime: number | null = null;
    let animationFrame: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      // easeOutExpo
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, isInView]);
  return <span ref={ref} className={className} data-id="element-140">
      {prefix}
      {count}
      {suffix}
    </span>;
}
// MagneticButton (Follows cursor slightly)
export function MagneticButton({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const {
      clientX,
      clientY
    } = e;
    const {
      height,
      width,
      left,
      top
    } = ref.current.getBoundingClientRect();
    const middleX = clientX - (left + width / 2);
    const middleY = clientY - (top + height / 2);
    // Max displacement ~8px
    setPosition({
      x: middleX * 0.1,
      y: middleY * 0.1
    });
  };
  const handleMouseLeave = () => {
    setPosition({
      x: 0,
      y: 0
    });
  };
  return <motion.div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} animate={{
    x: position.x,
    y: position.y
  }} transition={{
    type: 'spring',
    stiffness: 150,
    damping: 15,
    mass: 0.1
  }} className={`inline-block ${className}`} data-id="element-141">
      {children}
    </motion.div>;
}
// GradientText (Animated gradient text)
export function GradientText({
  text,
  className = ''
}: {
  text: string;
  className?: string;
}) {
  return <span className={`animate-gradient-shift bg-[linear-gradient(to_right,#00d4aa,#3b82f6,#00d4aa)] bg-[length:200%_auto] bg-clip-text text-transparent ${className}`} data-id="element-142">
      {text}
    </span>;
}
// Noise (Subtle film grain overlay)
export function Noise() {
  return <div className="pointer-events-none fixed inset-0 z-50 h-full w-full opacity-[0.03]" data-id="element-143">
      <svg className="absolute inset-0 h-full w-full" data-id="element-144">
        <filter id="noiseFilter" data-id="element-145">
          <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" data-id="element-146" />
        </filter>
        <rect width="100%" height="100%" filter="url(#noiseFilter)" data-id="element-147" />
      </svg>
    </div>;
}